import { useMemo } from "react";
import type { Column, Table as TanStackTable, VisibilityState } from "@tanstack/react-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Settings2, RotateCcw, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// SortableColumnItem component for drag-and-drop
function SortableColumnItem<T>({ 
  column, 
  label 
}: { 
  column: Column<T, unknown>; 
  label: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-2"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <Checkbox
        id={column.id}
        checked={column.getIsVisible()}
        onCheckedChange={(checked) => {
          column.toggleVisibility(!!checked);
        }}
      />
      <Label
        htmlFor={column.id}
        className="text-sm font-normal cursor-pointer flex-1"
      >
        {label}
      </Label>
    </div>
  );
}

interface ColumnConfigDialogProps<T> {
  table: TanStackTable<T>;
  columnLabels: Record<string, string>;
  columnOrder: string[];
  setColumnOrder: (order: string[]) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  columns: any[];
  onReset: () => void;
}

export function ColumnConfigDialog<T>({
  table,
  columnLabels,
  columnOrder,
  setColumnOrder,
  onReset,
}: ColumnConfigDialogProps<T>) {
  // Set up drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get ordered columns for the dialog
  const orderedColumns = useMemo(() => {
    const allColumns = table.getAllLeafColumns().filter((column) => column.id !== "select");
    
    if (columnOrder.length === 0) {
      return allColumns;
    }

    const columnMap = new Map(allColumns.map(col => [col.id, col]));
    const ordered: Column<T, unknown>[] = [];
    const usedIds = new Set<string>();
    
    for (const id of columnOrder) {
      const col = columnMap.get(id);
      if (col) {
        ordered.push(col);
        usedIds.add(id);
      }
    }
    
    for (const col of allColumns) {
      if (!usedIds.has(col.id)) {
        ordered.push(col);
      }
    }
    
    return ordered;
  }, [table, columnOrder]);

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedColumns.findIndex(col => col.id === active.id);
    const newIndex = orderedColumns.findIndex(col => col.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(
        orderedColumns.map(col => col.id),
        oldIndex,
        newIndex
      );
      setColumnOrder(newOrder);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex-shrink-0">
          <Settings2 className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Configure Columns</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Configure Columns</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              className="h-8"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
          </div>
        </DialogHeader>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedColumns.map(col => col.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 py-4">
              {orderedColumns.map((column) => {
                const headerLabel = columnLabels[column.id] || column.id;
                
                return (
                  <SortableColumnItem
                    key={column.id}
                    column={column}
                    label={headerLabel}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </DialogContent>
    </Dialog>
  );
}

